import { useAuthStore } from '../stores/auth';
import type { ApiResponse } from '@gitcenter/shared';

// 生产环境使用 Worker URL，开发环境使用本地代理
const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1';

interface RequestOptions extends RequestInit {
    skipAuth?: boolean;
}

/**
 * 统一的 API 请求函数
 */
export async function apiRequest<T>(
    endpoint: string,
    options: RequestOptions = {}
): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...((fetchOptions.headers as Record<string, string>) || {}),
    };

    // 添加认证头
    if (!skipAuth) {
        const sessionId = useAuthStore.getState().sessionId;
        if (sessionId) {
            headers['X-Session-Id'] = sessionId;
        }
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...fetchOptions,
        headers,
    });

    // 非 JSON 的 401 响应（如中间件直接返回文本）也需要拦截
    if (response.status === 401 && !skipAuth) {
        const store = useAuthStore.getState();
        if (store.isAuthenticated) {
            store.logout();
            window.location.href = '/login';
        }
        throw new ApiError('UNAUTHORIZED', '登录已过期，请重新登录', 401);
    }

    const contentType = response.headers.get('content-type') || '';
    let data: ApiResponse<T>;
    try {
        if (contentType.includes('application/json')) {
            data = (await response.json()) as ApiResponse<T>;
        } else {
            const text = await response.text();
            throw new ApiError(
                'INVALID_RESPONSE',
                response.ok ? '响应格式错误' : text || `请求失败 (${response.status})`,
                response.status,
                { body: text.slice(0, 200) }
            );
        }
    } catch (e) {
        if (e instanceof ApiError) throw e;
        throw new ApiError(
            'NETWORK_ERROR',
            e instanceof Error ? e.message : '请求失败',
            response.status,
            undefined
        );
    }

    if (!response.ok || !data.success) {
        const err = data.error;
        const errorMessage =
            (err && typeof err === 'object' && 'message' in err)
                ? (err as { message: string }).message
                : (typeof err === 'string' ? err : null)
                || `请求失败 (${response.status})`;
        const errorCode =
            (err && typeof err === 'object' && 'code' in err)
                ? (err as { code: string }).code
                : 'UNKNOWN_ERROR';
        const error = new ApiError(errorCode, errorMessage, response.status, data);
        throw error;
    }

    return data.data as T;
}

/**
 * API 错误类
 */
export class ApiError extends Error {
    constructor(
        public code: string,
        message: string,
        public status: number,
        public data?: any
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/**
 * GET 请求
 */
export function get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return apiRequest<T>(endpoint, { ...options, method: 'GET' });
}

/** 会话内 GET 缓存（TTL 毫秒），用于偏慢的只读接口 */
const getCache = new Map<string, { data: unknown; expiresAt: number }>();
const DEFAULT_TTL_MS = 2 * 60 * 1000; // 2 分钟

export function getWithCache<T>(endpoint: string, ttlMs: number = DEFAULT_TTL_MS): Promise<T> {
    const now = Date.now();
    const entry = getCache.get(endpoint);
    if (entry && entry.expiresAt > now) return Promise.resolve(entry.data as T);
    return get<T>(endpoint).then((data) => {
        getCache.set(endpoint, { data, expiresAt: now + ttlMs });
        return data;
    });
}

/** 写入/删除仓库或分支后可使 repo/branches/home 缓存失效 */
export function invalidateRepoCache(repoPath: string): void {
    getCache.delete(`/repos/${repoPath}`);
    getCache.delete(`/repos/${repoPath}/branches`);
    getCache.delete(`/repos/${repoPath}/home`);
    getCache.delete(`/repos/${repoPath}/tags`);
}

/** 预取仓库信息与分支列表（hover 时调用，首次打开时命中缓存） */
export function prefetchRepo(repoPath: string): void {
    if (!repoPath || repoPath.startsWith('/')) return;
    repoApi.get(repoPath).catch(() => {});
    repoApi.getBranches(repoPath).catch(() => {});
}

/**
 * POST 请求
 */
export function post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return apiRequest<T>(endpoint, {
        ...options,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
    });
}

/**
 * PUT 请求
 */
export function put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return apiRequest<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
    });
}

/**
 * DELETE 请求
 */
export function del<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
}

// ============================================
// API 函数
// ============================================

// 认证
export const authApi = {
    login: (username: string, password: string, totpCode?: string) =>
        post<{ user: any; sessionId: string }>('/auth/login', { username, password, totpCode }, { skipAuth: true }),

    register: (username: string, email: string, password: string) =>
        post<{ user: any; sessionId: string }>('/auth/register', { username, email, password }, { skipAuth: true }),

    bind2FA: (username: string, password: string, code: string, secret: string) =>
        post<{ user: any; sessionId: string }>('/auth/bind-2fa', { username, password, code, secret }, { skipAuth: true }),

    logout: () => post('/auth/logout'),
};

// 用户
export const userApi = {
    getCurrent: () => get<any>('/user'),
    update: (data: { email?: string; avatarUrl?: string }) => put<any>('/user', data),
    getByUsername: (username: string) => get<any>(`/users/${username}`),
    getRepos: (username: string) => get<any[]>(`/users/${username}/repos`),
    search: (query: string) => get<any[]>(`/users/search?q=${encodeURIComponent(query)}`),
    getCollaborations: () => get<any[]>('/user/collaborations'),
};

// 群组
export const groupApi = {
    list: (page = 1, pageSize = 20, options?: { parentId?: string }) => {
        let url = `/groups?page=${page}&pageSize=${pageSize}`;
        if (options?.parentId !== undefined) {
            url += `&parentId=${encodeURIComponent(options.parentId)}`;
        }
        return get<{ items: any[]; total: number }>(url);
    },

    get: (path: string) => get<any>(`/groups/${path}`),

    /** 一次请求拉取群组及子资源（降低延迟，减少请求数） */
    getWithInclude: (path: string, include: ('subgroups' | 'projects' | 'members')[] = ['subgroups', 'projects', 'members']) =>
        get<{ group: any; subgroups?: any[]; projects?: any[]; members?: any[] }>(
            `/groups/${path}?include=${include.join(',')}`
        ),

    create: (data: { name: string; path?: string; description?: string; visibility?: string; parentId?: string }) =>
        post<any>('/groups', data),

    createSubgroup: (parentPath: string, data: { name: string; description?: string }) =>
        post<any>(`/groups/${parentPath}/subgroups`, data),

    getSubgroups: (path: string) => get<any[]>(`/groups/${path}/subgroups`),

    getProjects: (path: string) => get<any[]>(`/groups/${path}/projects`),

    getMembers: (path: string) => get<any[]>(`/groups/${path}/members`),

    addMember: (path: string, userId: string, role: string) =>
        post<any>(`/groups/${path}/members`, { userId, role }),

    removeMember: (path: string, userId: string) =>
        del(`/groups/${path}/members/${userId}`),

    delete: (path: string) => del(`/groups/${path}`),
};

// 仓库（repo/branches/tags 使用 2 分钟 TTL 缓存，减少重复请求）
const REPO_CACHE_TTL = 2 * 60 * 1000;

export interface RepoHomeData {
    repo: any;
    branches: any[];
}

export const repoApi = {
    get: (path: string) => getWithCache<any>(`/repos/${path}`, REPO_CACHE_TTL),

    /** 聚合接口：一次返回 repo + branches，用于仓库首页首屏 */
    getHome: (path: string) => getWithCache<RepoHomeData>(`/repos/${path}/home`, REPO_CACHE_TTL),

    create: (data: { name: string; description?: string; isPrivate?: boolean }) =>
        post<any>('/user/repos', data),

    createInGroup: (groupPath: string, data: { name: string; description?: string; isPrivate?: boolean }) =>
        post<any>(`/groups/${groupPath}/repos`, data),

    update: (path: string, data: { description?: string; isPrivate?: boolean; defaultBranch?: string }) =>
        put<any>(`/repos/${path}`, data),

    delete: (path: string) => del(`/repos/${path}`),

    getBranches: (path: string) => getWithCache<any[]>(`/repos/${path}/branches`, REPO_CACHE_TTL),

    getTags: (path: string) => getWithCache<any[]>(`/repos/${path}/tags`, REPO_CACHE_TTL),

    createBranch: (path: string, name: string, sha: string) =>
        post<any>(`/repos/${path}/branches`, { name, sha }).then((r) => {
            invalidateRepoCache(path);
            return r;
        }),

    deleteBranch: (path: string, branch: string) =>
        del(`/repos/${path}/branches/${branch}`).then((r) => {
            invalidateRepoCache(path);
            return r;
        }),

    merge: (path: string, source: string, target: string) =>
        post<any>(`/repos/${path}/merges`, { source, target }),

    /** 提交历史（1 分钟缓存） */
    getCommits: (path: string, ref: string, page: number) =>
        getWithCache<any>(`/repos/${path}/commits/${encodeURIComponent(ref)}?page=${page}`, 60 * 1000),

    /** 单次提交详情（2 分钟缓存） */
    getCommit: (path: string, sha: string) =>
        getWithCache<any>(`/repos/${path}/commit/${sha}`, REPO_CACHE_TTL),

    /** 提交 diff（2 分钟缓存） */
    getDiff: (path: string, sha: string) =>
        getWithCache<any>(`/repos/${path}/diff/${sha}`, REPO_CACHE_TTL),
};

// 协作者
export const collaboratorApi = {
    list: (repoPath: string) => get<any[]>(`/repos/${repoPath}/collaborators`),

    add: (repoPath: string, data: { username?: string; email?: string; role: string }) =>
        post<any>(`/repos/${repoPath}/collaborators`, data),

    update: (repoPath: string, userId: string, role: string) =>
        put<any>(`/repos/${repoPath}/collaborators/${userId}`, { role }),

    remove: (repoPath: string, userId: string) =>
        del(`/repos/${repoPath}/collaborators/${userId}`),
};

// PAT Token 管理
export interface TokenItem {
    id: string;
    name: string;
    scopes: string[];
    expiresAt?: string;
    createdAt: string;
}

export interface CreatedToken extends TokenItem {
    token: string; // 仅创建时返回一次
}

export const tokenApi = {
    list: () => get<TokenItem[]>('/auth/tokens'),

    create: (data: { name: string; scopes: string[]; expiresAt?: string }) =>
        post<CreatedToken>('/auth/tokens', data),

    revoke: (id: string) => del<void>(`/auth/tokens/${id}`),
};

// 合并请求
export const pullRequestApi = {
    compare: (repoPath: string, source: string, target: string) =>
        get<any>(`/repos/${repoPath}/compare?source=${encodeURIComponent(source)}&target=${encodeURIComponent(target)}`),

    create: (repoPath: string, data: { title: string; description?: string; sourceBranch: string; targetBranch: string; isDraft?: boolean }) =>
        post<any>(`/repos/${repoPath}/pulls`, data),

    list: (repoPath: string, status?: string) =>
        get<any[]>(`/repos/${repoPath}/pulls${status ? `?status=${status}` : ''}`),

    get: (repoPath: string, id: string) =>
        get<any>(`/repos/${repoPath}/pulls/${id}`),

    merge: (repoPath: string, id: string) =>
        post<any>(`/repos/${repoPath}/pulls/${id}/merge`),
};
